import type { RatePolicy, RatePolicyLiveStats } from '@zyx/shared';
import { Lock, Pencil, Plus, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { ErrorState, LoadingRows } from '@/components/common/states';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRatePolicies } from '@/hooks/use-indexing';
import { PolicyDialog } from './policy-dialog';
import { Sparkline } from './sparkline';

function perSecond(value: number): string {
  return `${value < 10 ? value.toFixed(2) : value.toFixed(0)}/s`;
}

/** Seconds between the points of each policy's `recent` series. */
const SLICE_S = 5;

/** One policy's row; a fixed one says what it is for instead of offering an edit. */
function PolicyRow(props: { readonly policy: RatePolicy; readonly stats?: RatePolicyLiveStats; readonly onEdit?: () => void }) {
  const { policy, stats } = props;
  const limit = (policy.requests * 1000) / policy.windowMs;
  const backingOff = stats?.backingOffUntil !== undefined;
  const errors = stats === undefined ? 0 : stats.outcomes['4xx'] + stats.outcomes['5xx'] + stats.outcomes.network;
  return (
    <TableRow className={policy.enabled ? undefined : 'opacity-60'}>
      <TableCell className="font-medium">{policy.policyId}</TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {props.onEdit === undefined ? <span className="font-sans">watching, on its own</span> : policy.hostPatterns.length > 0 ? policy.hostPatterns.join(' ') : 'named by a source'}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {perSecond(limit)} · {policy.maxConcurrency} at once
        {policy.enabled ? null : <span className="ml-1 text-xs text-muted-foreground">(off)</span>}
      </TableCell>
      <TableCell>
        <Sparkline values={stats?.recent ?? []} stepS={SLICE_S} limit={limit} label={policy.policyId} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-right tabular-nums">
        {perSecond(stats?.requestsPerSecond ?? 0)}
        {backingOff ? (
          <span className="ml-1.5 inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400" title="The source pushed back; the rate is reduced for now.">
            <TriangleAlert className="size-3.5" />
            slowed to {perSecond(stats?.effectiveRate ?? 0)}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {stats?.waiting ?? 0}
        <span className="text-muted-foreground"> · {stats?.inFlight ?? 0} running</span>
      </TableCell>
      <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
        {errors === 0 || stats === undefined ? '—' : `4xx ${stats.outcomes['4xx']} · 5xx ${stats.outcomes['5xx']} · net ${stats.outcomes.network}`}
      </TableCell>
      <TableCell>
        {props.onEdit === undefined ? (
          <span className="inline-flex size-9 items-center justify-center text-muted-foreground" title="Set by zyx: watching never queues behind a crawl.">
            <Lock className="size-4" />
          </span>
        ) : (
          <Button variant="ghost" size="icon" aria-label={`Edit ${policy.policyId}`} onClick={props.onEdit}>
            <Pencil className="size-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Every rate policy, how fast it is sending right now, and the way to change it. */
export function RateTable() {
  const query = useRatePolicies();
  const [editing, setEditing] = useState<RatePolicy | 'new' | undefined>(undefined);

  if (query.isPending) {
    return <LoadingRows rows={4} />;
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const live = new Map(query.data.live.map((stats) => [stats.policyId, stats]));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Each request to a source waits for its policy, shared by every job. Hosts that rotate are charged to a policy the source names. Watching a movie has a budget of its own.
        </p>
        <Button variant="outline" size="sm" onClick={() => setEditing('new')}>
          <Plus className="size-4" />
          New policy
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Policy</TableHead>
              <TableHead>Hosts</TableHead>
              <TableHead>Limit</TableHead>
              <TableHead>Last minute</TableHead>
              <TableHead className="text-right">Now</TableHead>
              <TableHead className="text-right">Queued</TableHead>
              <TableHead className="text-right">Errors (1 min)</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.data.policies.map((policy) => (
              <PolicyRow key={policy.policyId} policy={policy} stats={live.get(policy.policyId)} onEdit={() => setEditing(policy)} />
            ))}
            {/* A service older than the page sends no fixed policies. */}
            {(query.data.fixed ?? []).map((policy) => (
              <PolicyRow key={policy.policyId} policy={policy} stats={live.get(policy.policyId)} />
            ))}
          </TableBody>
        </Table>
      </div>
      <PolicyDialog
        policy={editing === 'new' ? undefined : editing}
        existingIds={query.data.policies.map((policy) => policy.policyId)}
        open={editing !== undefined}
        onOpenChange={(open) => (open ? undefined : setEditing(undefined))}
      />
    </div>
  );
}
